import { ScrollObserver } from '../lib/scroll-observer'

const LG_MEDIA_QUERY = '(min-width: 64rem)'
const XL_MEDIA_QUERY = '(min-width: 80rem)'

export class Header {
  $element: HTMLElement
  $menu: HTMLElement
  $menuToggle: HTMLButtonElement
  $firstMenuFocusable: HTMLElement
  $stickySentinel: HTMLElement
  menuOpen: boolean
  private stickyObserver: ScrollObserver | null = null
  private readonly lgMediaQuery: MediaQueryList
  private readonly xlMediaQuery: MediaQueryList

  constructor($element: HTMLElement) {
    this.$element = $element
    this.menuOpen = false

    this.$menu = this.$element.querySelector('.header__menu') as HTMLElement
    this.$menuToggle = this.$element.querySelector(
      '.header__menu-toggle',
    ) as HTMLButtonElement
    this.$stickySentinel = this.$element.querySelector(
      '.header__sticky-sentinel',
    ) as HTMLElement

    this.$firstMenuFocusable = this.$menu.querySelector(
      'a, button',
    ) as HTMLElement

    this.lgMediaQuery = window.matchMedia(LG_MEDIA_QUERY)
    this.xlMediaQuery = window.matchMedia(XL_MEDIA_QUERY)

    this.$menuToggle.addEventListener('click', this.toggleMenu.bind(this, undefined))
    this.$menu.addEventListener('focusout', this.handleMenuFocusOut.bind(this))

    if (this.$stickySentinel !== null) {
      const onStickyLayoutChange = this.handleStickyBreakpointChange.bind(this)
      this.lgMediaQuery.addEventListener('change', onStickyLayoutChange)
      this.xlMediaQuery.addEventListener('change', onStickyLayoutChange)
      this.handleStickyBreakpointChange()
    }
  }

  private handleStickyBreakpointChange(): void {
    if (this.lgMediaQuery.matches) {
      this.initStickyObserver()
      return
    }

    this.stickyObserver?.disconnect()
    this.stickyObserver = null
    this.$element.classList.remove('header--sticky')
  }

  private initStickyObserver(): void {
    this.stickyObserver?.disconnect()

    this.stickyObserver = new ScrollObserver(
      this.$stickySentinel,
      (isIntersecting) => {
        // No rootMargin: sticky only when the sentinel leaves the viewport (menu top at y = 0).
        this.$element.classList.toggle('header--sticky', !isIntersecting)
      },
    )
  }

  handleMenuFocusOut(event: FocusEvent): void {
    if (!this.menuOpen) {
      return
    }

    // Defer so link clicks complete before the menu closes
    window.setTimeout(() => {
      if (
        event.relatedTarget === null ||
        !this.$menu.contains(event.relatedTarget as Node)
      ) {
        this.toggleMenu(false)
      }
    }, 0)
  }

  toggleMenu(menuOpen?: boolean): void {
    if (menuOpen === this.menuOpen) {
      return
    }

    this.menuOpen = menuOpen ?? !this.menuOpen

    this.$element.classList.toggle('header--menu-open', this.menuOpen)

    if (this.menuOpen) {
      // Focus the first focusable element in the menu
      if (this.$firstMenuFocusable) {
        this.$firstMenuFocusable.focus()
      }
    }
  }
}
